<template>
  <div>
    <div class="mb-4">
      <h4 class="font-weight-bold mb-2">
        <span class="text-muted font-weight-light">Autofixes <span class="material-icons md-14 arrow-east">east</span></span> library
      </h4>
    </div>
    <div v-if="!loading">
      <div v-if="availableCategories">
        <b-form-radio-group
          id="tooltipLabels"
          name="tooltipLabels"
          v-model="selectedCategory"
          :options="availableCategories"
          buttons
          class="mb-4"
        ></b-form-radio-group>
      </div>
      <h4 class="card-title line-after mb-4">{{ selectedCategoryName }} autofixes</h4>
      <div class="card-deck card-deck-500">
        <div class="card mb-4" v-for="template in filteredTemplates" :key="template.id" :style="{ 'opacity': template.status !== 'ACTIVE' ? 0.6 : 1 }">
          <div class="card-body py-0">
            <div class="d-flex mt-4 justify-content-between">
              <h5 class="card-title">
                <router-link v-if="canEdit" :to="{ name: 'af-templates-edit', params: { id: template.id } }" class="text-body">
                  {{ template.name }}
                </router-link>
                <template v-else>
                  {{ template.name }}
                </template>
              </h5>
              <div style="text-align: right;">
                <router-link class="btn btn-iso-create" :to="{ name: 'af-forms-add', query: { template: template.id, form: $route.query.form } }" v-if="canEdit">Select</router-link>
                <div v-if="$root.hasRole('admin.internal') && template.status !== 'ACTIVE'">
                  <span class="badge" :class="statusColors[template.status]">{{ template.status }}</span>
                </div>
              </div>
            </div>
            <img v-if="template.screenshots[0] && template.screenshots[0].screenshot_uri" @click="showScreenshot = template.screenshots[0]; showScreenshotModal = true;" :src="template.screenshots[0].screenshot_uri" style="max-width: 452px;" :title="template.name" :alt="template.name" class="img-fluid cursor-pointer" />
            <b-modal
                v-model="showScreenshotModal"
                @close="showScreenshotModal = false; showScreenshot = null;"
                v-if="showScreenshot"
                size="xl"
                hide-footer
                centered
            >
              <img
                  :src="showScreenshot.screenshot_uri"
                  class="img-fluid"
               alt=""/>
            </b-modal>
            <div v-html="template.description" class="mt-3"></div>
            <div class="position-relative">
              <div v-if="canEdit" class="position-absolute w-100 h-100 unselectable cursor-pointer" style="left: 0; top: 0;" @click="$router.push({ name: 'af-forms-add', query: { template: template.id, form: $route.query.form } })"></div>
              <div v-else class="position-absolute w-100 h-100 unselectable" style="left: 0; top: 0;"></div>
            </div>
          </div>
          <div class="card-footer">
            <div class="d-flex">
              <div class="flex-fill">
              <span class="badge badge-outline-secondary bg-light mr-1" v-for="category in template.categories"
                    :key="category.id">{{ category.name }}</span>
              </div>
              <div class="align-self-end text-nowrap text-big" v-if="$root.hasRole('admin.internal')">
                <router-link v-if="canEdit" :to="{ name: 'af-templates-edit', params: { id: template.id } }" class="text-body adminOnly">
                  <i class="cursor-pointer ion ion-md-create"></i>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AutofixLibrary',
  metaInfo: {
    title: 'Autofixes - library'
  },
  data () {
    return {
      loading: false,
      categories: [],
      selectedCategory: null,
      templates: [],
      statusColors: {
        ACTIVE: 'badge-success',
        ARCHIVED: 'badge-dark',
        INACTIVE: 'badge-dark',
        NODATA: 'badge-danger'
      },
      showScreenshot: null,
      showScreenshotModal: false
    }
  },
  computed: {
    canEdit () {
      return this.$root.hasRole('admin.internal') ||
        this.$root.hasRole('admin.company') ||
        this.$root.hasRole('configuration.modify')
    },
    availableCategories () {
      const tplCategories = this.templates.map(t => {
        if (t.status !== 'ACTIVE') return
        return t.categories.map(tc => tc.id)
      }).flat()
      const result = this.categories
        .filter(c => tplCategories.includes(c.id))
        .map(cat => ({
          text: cat.name,
          value: cat.id
        }))
        .sort((a, b) => {
          const aText = a.text.toLowerCase()
          const bText = b.text.toLowerCase()

          if (aText === 'popular' && bText !== 'popular') return -1
          if (bText === 'popular' && aText !== 'popular') return 1

          return aText.localeCompare(bText)
        })
      result.unshift({
        text: 'All',
        value: null
      })
      return result
    },
    selectedCategoryName () {
      return this.selectedCategory ? this.categories.filter(c => c.id === this.selectedCategory)[0].name : 'All'
    },
    filteredTemplates () {
      return this.selectedCategory ? this.templates.filter(tpl => tpl.categories.map(c => c.id).includes(this.selectedCategory)) : this.templates
    }
  },
  methods: {
    async reload () {
      if (!this.$root.client) return

      this.loading = true

      const [
        categories,
        templates
      ] = await Promise.all([
        this.$api.get('/interaction-categories', this.$root.client.client_hash),
        this.$api.get('/interaction-templates/autofixes', this.$root.client.client_hash)
      ])
      this.categories = categories.data
      this.templates = templates.data.map((template) => {
        const isOwnTemplate = template.client_hash === this.$root.client.client_hash
        const isSharedTemplate = template.client_hash === null
        if (!isOwnTemplate && !isSharedTemplate) return null

        return {
          ...template,
          categories: template.categories || [],
          preview: this.getTemplatePreview(template)
        }
      })
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name))

      this.loading = false
    },
    getTemplatePreview (interaction) {
      const configuration = (interaction.default_interaction_configurations || [])
        .filter(function (config) { return (config.interaction_configuration_type_hash === 'a7a5dd23-8411-4af8-a346-d616482fb14b') }) // FFN_CONFIGURATION
        .map(function (config) { return config.configuration[0] })

      return this.$api.eventPost(`/templates/preview?clientCode=${this.$root.client.client_code}`, {
        template: interaction.template,
        FFN_CONFIGURATION: configuration.length ? configuration[0] : []
      })
        .then(response => {
          setTimeout(() => this.resizeIframe(document.getElementById(interaction.id)), 250)
          return response
        })
        .catch(e => {
          // do nothing, screenshot not crucial
        })
    },
    async resizeIframe (iframe, retry = false) {
      if (!iframe) return
      if (typeof iframe === 'string') {
        iframe = document.getElementById(iframe)
      }
      if (typeof iframe.contentWindow.document.body !== 'undefined' && iframe.contentWindow.document.body) {
        iframe.contentWindow.document.body.style.margin = 20 + 'px'
        iframe.contentWindow.document.body.style.overflow = 'scroll'
        iframe.height = (iframe.contentWindow.document.body.scrollHeight + 40) + 'px'
        iframe.style.height = iframe.height
      } else if (!retry) {
        setTimeout(() => {
          this.resizeIframe(iframe, true)
        }, 2500)
      }
    }

  },
  watch: {
    '$root.client': {
      async handler () {
        await this.reload()
      },
      immediate: true
    }
  }
}
</script>

<style scoped>
.card-deck {
  align-items: flex-start;
}
.card-deck-500 .card {
  flex: 0 0 500px;
}
.card:hover {
  opacity: 1 !important;
}
</style>
<style>
#tooltipLabels .btn {
  margin-right: 1rem;
  border: 0 solid transparent;
  border-radius: 40px !important;
}
#tooltipLabels label.active {
  background-color: var(--exatom-green) !important;
  color: var(--exatom-blue-min1) !important;
}
</style>
